import type { ComponentProps } from "react";
import { tv } from "tailwind-variants";

import type { ProgressSideEffectEntry } from "@/domains/progress/models/types";
import {
  formatProgressDate,
  orFallback,
  PROGRESS_SIDE_EFFECT_NO_ACTION,
  PROGRESS_SIDE_EFFECT_NO_PRODUCT,
  PROGRESS_SIDE_EFFECTS_TITLE,
} from "@/domains/progress/utils/progress-page-copy";
import { Accordion, Card } from "@/ui";

const ACCORDION_ITEM_VALUE = "progress-side-effects";

const cardLayout = tv({
  base: "px-5 py-1 sm:px-6",
});

export type ProgressSideEffectsLogProps = ComponentProps<"article"> & {
  entries: ProgressSideEffectEntry[];
};

export const ProgressSideEffectsLog = ({
  className,
  entries,
  ...props
}: ProgressSideEffectsLogProps) => {
  return (
    <Card className={cardLayout({ className })} {...props}>
      <Accordion.Root type="single" collapsible>
        <Accordion.Item value={ACCORDION_ITEM_VALUE}>
          <Accordion.Header asChild>
            <h2>
              <Accordion.Trigger>
                {PROGRESS_SIDE_EFFECTS_TITLE}

                <Accordion.Icon />
              </Accordion.Trigger>
            </h2>
          </Accordion.Header>

          <Accordion.Content className="text-content sm:text-base">
            <ul className="flex flex-col gap-4">
              {entries.map((entry) => {
                const reportedOn = formatProgressDate(entry.reportedAt);

                return (
                  <li
                    className="flex flex-col gap-1 border-b border-line pb-4 last:border-b-0 last:pb-0"
                    key={entry.id}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <p className="font-semibold text-content">
                        {orFallback(entry.effect, entry.effectCode)}
                      </p>

                      {reportedOn === null ? null : (
                        <p className="text-sm text-content-secondary">{reportedOn}</p>
                      )}
                    </div>

                    <p className="text-content-secondary">
                      {orFallback(entry.product?.name, PROGRESS_SIDE_EFFECT_NO_PRODUCT)}
                    </p>

                    <p className="text-content-secondary">
                      {orFallback(entry.actionText, PROGRESS_SIDE_EFFECT_NO_ACTION)}
                    </p>
                  </li>
                );
              })}
            </ul>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion.Root>
    </Card>
  );
};
